import { create } from "zustand";
import { TextData, TextSortData } from "../dto/EntityRepository";
import { PerPage, perPageToNumber, SortDirection, TextSortOrder } from '../enum/EnumRepository';
import type { ResourceScope, ResourceStatus } from '../util/scopedResource';

interface TextListState {
  pageSize: PerPage;
  setPageSize: (pageSize: PerPage) => void;
  sortData: TextSortData;
  setSortData: (sortData: TextSortData) => void;
  textList: TextData[];
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  status: ResourceStatus;
  setTextList: (textList: TextData[]) => void;
  beginTextListFetch: (ownerAui: string, scope: ResourceScope) => void;
  setTextListWithScope: (
    textList: TextData[],
    ownerAui: string,
    scope: ResourceScope,
  ) => void;
  setTextListError: () => void;
  addText: (text: TextData, pageSize: PerPage) => void;
  updateAfterIsPublicToggle: (textId: string, isPublic: boolean) => void;
  clearTextList: () => void;
}

const isSameText = (prev: TextData, next: TextData): boolean =>
  prev.id === next.id &&
  prev.title === next.title &&
  prev.subtitle === next.subtitle &&
  prev.isPublic === next.isPublic &&
  prev.prodYear === next.prodYear &&
  prev.source.author === next.source.author &&
  prev.source.title === next.source.title;

const isSameTextList = (prev: TextData[], next: TextData[]): boolean =>
  prev.length === next.length &&
  prev.every((text, index) => isSameText(text, next[index]));

/** 본문 Texts 첫 랜딩: index 지정 Text 우선, 나머지는 최신 연도순 */
export const getDefaultTextSortData = (): TextSortData => ({
  sort: TextSortOrder.YEAR,
  direction: SortDirection.DESC,
  manualOrderFirst: true,
});

/** import 모달 기본 정렬 */
export const getDefaultModalTextSortData = (): TextSortData => ({
  sort: TextSortOrder.MANUAL_ORDER,
  direction: SortDirection.ASC,
});

export const useTextListStore = create<TextListState>((set, get) => ({
  pageSize: PerPage.PAGE_10,
  setPageSize: (pageSize) => set({ pageSize }),
  sortData: getDefaultTextSortData(),
  setSortData: (sortData) => set({ sortData }),
  textList: [],
  listOwnerAui: null,
  listScope: null,
  status: 'idle',
  setTextList: (textList) => set({ textList }),
  beginTextListFetch: (ownerAui, scope) => set((state) => {
    // 같은 owner+scope 재fetch: success 유지(SWR). 다르면 loading으로 숨김.
    if (
      state.listOwnerAui === ownerAui &&
      state.listScope === scope &&
      state.status === 'success'
    ) {
      return state;
    }
    return { status: 'loading' };
  }),
  setTextListWithScope: (textList, ownerAui, scope) =>
    set((state) => {
      if (
        state.status === 'success' &&
        state.listOwnerAui === ownerAui &&
        state.listScope === scope &&
        isSameTextList(state.textList, textList)
      ) {
        return state;
      }
      return {
        textList,
        listOwnerAui: ownerAui,
        listScope: scope,
        status: 'success',
      };
    }),
  setTextListError: () => set({ status: 'error' }),
  addText: (text, pageSize: PerPage) => {
    const currentList = get().textList.filter((t) => t.id !== text.id);
    const pageSizeNumber = perPageToNumber(pageSize);
    const newList = [text, ...currentList].slice(0, pageSizeNumber);
    set({ textList: newList });
  },
  updateAfterIsPublicToggle: (textId, isPublic) => {
    const currentList = get().textList;
    const newList = currentList.map((text) =>
      text.id === textId ? { ...text, isPublic } : text
    );
    set({ textList: newList });
  },
  clearTextList: () => set((state) => {
    if (
      state.textList.length === 0 &&
      state.listOwnerAui === null &&
      state.listScope === null &&
      state.status === 'idle'
    ) {
      return state;
    }
    return {
      textList: [],
      listOwnerAui: null,
      listScope: null,
      status: 'idle',
    };
  }),
}));
